import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Grid,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Stepper,
  Step,
  StepLabel,
} from '@mui/material';
import {
  ArrowBack,
  Save,
} from '@mui/icons-material';
import  {itemService}  from '../../services/itemService';
import { useSnackbar } from 'notistack';

export const ItemForm = ({ item = null, isEdit = false }) => {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const [loading, setLoading] = useState(false);
  const [activeStep, setActiveStep] = useState(0);
  
  const [formData, setFormData] = useState({
    // Basic Information
    name: item?.name || '',
    brand: item?.brand || '',
    manufacturer: item?.manufacturer || '',
    itemType: item?.itemType || 'consumable',
    unit: item?.unit || 'pieces',
    category: item?.category || '',
    
    // Identification
    barcode: item?.barcode || '',
    hsnCode: item?.hsnCode || '',
    
    // Description
    description: item?.description || '',
    
    // Storage
    storageConditions: {
      temperature: item?.storageConditions?.temperature || 'Room Temperature',
      humidity: item?.storageConditions?.humidity || 'Normal',
      lightSensitive: item?.storageConditions?.lightSensitive || false,
      refrigerated: item?.storageConditions?.refrigerated || false,
    },
  });

  const steps = ['Basic Information', 'Identification & Details', 'Storage Conditions'];

  const handleInputChange = (field, value) => {
    if (field.includes('.')) {
      const [parent, child] = field.split('.');
      setFormData(prev => ({
        ...prev,
        [parent]: {
          ...prev[parent],
          [child]: value
        }
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [field]: value
      }));
    }
  };

  const handleSubmit = async () => {
    setLoading(true);
    try {
      if (isEdit && item) {
        await itemService.updateItem(item._id, formData);
        enqueueSnackbar('Item updated successfully!', { variant: 'success' });
      } else {
        await itemService.createItem(formData);
        enqueueSnackbar('Item created successfully!', { variant: 'success' });
      }
      navigate('/hospital/hospital-inventory/items');
    } catch (error) {
      enqueueSnackbar(`Error ${isEdit ? 'updating' : 'creating'} item`, { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleNext = () => {
    setActiveStep((prev) => prev + 1);
  };

  const handleBack = () => {
    setActiveStep((prev) => prev - 1);
  };

  const formConfigs = {
    0: {
      title: 'Basic Information',
      fields: [
        { name: 'name', label: 'Item Name', required: true, fullWidth: true },
        { name: 'brand', label: 'Brand', required: true },
        { name: 'manufacturer', label: 'Manufacturer', required: true },
        { name: 'itemType', label: 'Item Type', required: true, type: 'select', options: [
          { value: 'consumable', label: 'Consumable' },
          { value: 'equipment', label: 'Equipment' },
          { value: 'raw_material', label: 'Raw Material' },
          { value: 'finished_good', label: 'Finished Good' },
          { value: 'other', label: 'Other' },
        ]},
        { name: 'unit', label: 'Unit of Measurement', required: true, type: 'select', options: [
          { value: 'pieces', label: 'Pieces' },
          { value: 'kg', label: 'Kilograms' },
          { value: 'liters', label: 'Liters' },
          { value: 'boxes', label: 'Boxes' },
          { value: 'meters', label: 'Meters' },
        ]},
        { name: 'category', label: 'Category', fullWidth: true },
      ]
    },
    1: {
      title: 'Identification & Details',
      fields: [
        { name: 'barcode', label: 'Barcode', fullWidth: true },
        { name: 'hsnCode', label: 'HSN Code' },
        { name: 'description', label: 'Description', type: 'textarea', fullWidth: true, rows: 4 },
      ]
    },
    2: {
      title: 'Storage Conditions',
      fields: [
        { name: 'storageConditions.temperature', label: 'Storage Temperature', value: formData.storageConditions.temperature },
        { name: 'storageConditions.humidity', label: 'Humidity Conditions', value: formData.storageConditions.humidity },
      ]
    }
  };

  const currentConfig = formConfigs[activeStep];

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 4 }}>
        <Button
          startIcon={<ArrowBack />}
          onClick={() => navigate('/hospital/hospital-inventory/items')}
          variant="outlined"
        >
          Back
        </Button>
        <Typography variant="h4" fontWeight="bold" sx={{ flexGrow: 1 }}>
          {isEdit ? 'Edit Item' : 'Add New Item'}
        </Typography>
      </Box>

      <Card>
        <CardContent>
          {/* Stepper */}
          <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>

          <Typography variant="h5" gutterBottom fontWeight="bold">
            {currentConfig.title}
          </Typography>

          <Grid container spacing={3}>
            {/* Dynamic Form Fields */}
            {currentConfig.fields.map((field) => (
              <Grid item xs={12} sm={field.fullWidth ? 12 : 6} key={field.name}>
                {field.type === 'select' ? (
                  <TextField
                    select
                    fullWidth
                    label={field.label}
                    value={formData[field.name]}
                    onChange={(e) => handleInputChange(field.name, e.target.value)}
                    required={field.required}
                  >
                    {field.options.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </TextField>
                ) : field.type === 'textarea' ? (
                  <TextField
                    fullWidth
                    multiline
                    rows={field.rows || 4}
                    label={field.label}
                    value={formData[field.name]}
                    onChange={(e) => handleInputChange(field.name, e.target.value)}
                  />
                ) : (
                  <TextField
                    fullWidth
                    label={field.label}
                    value={field.value || formData[field.name]}
                    onChange={(e) => handleInputChange(field.name, e.target.value)}
                    required={field.required}
                  />
                )}
              </Grid>
            ))}

            {/* Storage Conditions (Step 2) */}
            {activeStep === 2 && (
              <Grid item xs={12}>
                <Typography variant="h6" gutterBottom>
                  Additional Storage Settings
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <FormControl fullWidth>
                      <InputLabel>Light Sensitive</InputLabel>
                      <Select
                        value={formData.storageConditions.lightSensitive}
                        onChange={(e) => handleInputChange('storageConditions.lightSensitive', e.target.value)}
                        label="Light Sensitive"
                      >
                        <MenuItem value={false}>No</MenuItem>
                        <MenuItem value={true}>Yes</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <FormControl fullWidth>
                      <InputLabel>Refrigerated</InputLabel>
                      <Select
                        value={formData.storageConditions.refrigerated}
                        onChange={(e) => handleInputChange('storageConditions.refrigerated', e.target.value)}
                        label="Refrigerated"
                      >
                        <MenuItem value={false}>No</MenuItem>
                        <MenuItem value={true}>Yes</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                </Grid>
              </Grid>
            )}
          </Grid>

          {/* Navigation Buttons */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4 }}>
            <Button
              disabled={activeStep === 0}
              onClick={handleBack}
            >
              Back
            </Button>
            
            <Box sx={{ display: 'flex', gap: 2 }}>
              {activeStep === steps.length - 1 ? (
                <Button
                  variant="contained"
                  onClick={handleSubmit}
                  disabled={loading}
                  startIcon={<Save />}
                >
                  {loading ? (isEdit ? 'Updating...' : 'Creating...') : (isEdit ? 'Update Item' : 'Create Item')}
                </Button>
              ) : (
                <Button
                  variant="contained"
                  onClick={handleNext}
                >
                  Next
                </Button>
              )}
            </Box>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};
